import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, ShieldCheck, MessageCircle, ArrowRight, Leaf, BookOpen, FlaskConical, HeartHandshake } from 'lucide-react';

const features = [
  { icon: Search, text: 'Search remedies by symptom, age or profile' },
  { icon: Package, text: 'Build a natural first-aid kit tailored to your family' },
  { icon: ShieldCheck, text: 'Safety & contraindication checker built-in' },
  { icon: MessageCircle, text: 'Chat with Sage, your AI herbalist' }
];

const highlights = [
  { icon: BookOpen, title: '200+ herbs & oils', body: 'A gentle, searchable library of essential oils, carrier oils and healing herbs — each with benefits and safety notes.' },
  { icon: FlaskConical, title: '52 guided recipes', body: 'Teas, salves, tinctures and more. Filter by symptom, age and profile to find the right remedy fast.' },
  { icon: HeartHandshake, title: 'Safe by default', body: 'Every entry surfaces contraindications first, so you can care for your family with confidence.' }
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const runSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search');
  };

  return (
    <div>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-14 pb-16 md:pt-20 md:pb-24 grid md:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-botanica-green font-semibold mb-5">
            Symptom-first · Safe by default
          </p>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-botanica-ink mb-5">
            Your herbal<br />companion.
          </h1>
          <p className="text-botanica-muted text-base md:text-lg leading-relaxed max-w-md mb-7">
            Your Herbal Companion began with a simple wish: to make natural remedies feel approachable, trustworthy and safe. We blend the wisdom of traditional herbalism with clear, modern guidance — so you can care for the people you love with confidence.
          </p>

          <form onSubmit={runSearch} className="relative max-w-md mb-7">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a symptom — e.g. trouble sleeping"
              className="w-full pl-11 pr-24 py-3.5 rounded-full bg-botanica-panel border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
            />
            <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-2 rounded-full bg-botanica-green text-white text-sm font-medium hover:bg-botanica-greenDark transition-colors">
              Search
            </button>
          </form>

          <ul className="space-y-3.5 mb-8">
            {features.map((f) => (
              <li key={f.text} className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-botanica-greenLight flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4 text-botanica-green" />
                </span>
                <span className="text-sm text-botanica-ink">{f.text}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate('/membership')}
              className="group flex items-center gap-2 px-6 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors"
            >
              Create free account
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => navigate('/library')}
              className="px-6 py-3 rounded-full border border-botanica-card bg-botanica-panel text-botanica-ink font-medium hover:bg-botanica-card transition-colors"
            >
              Learn more
            </button>
          </div>
          <p className="text-xs italic text-botanica-muted mt-4">Educational only. Not medical advice.</p>
        </div>

        <div className="animate-fade-up" style={{ animationDelay: '120ms' }}>
          <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(60,60,40,0.35)]">
            <img
              src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
              alt="Young seedlings sprouting in soil"
              className="w-full h-[440px] md:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/35 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-botanica-greenLight" />
              <span className="text-xs text-white/90">Premium $1.99/mo — 7-day free trial</span>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-botanica-panel border-y border-botanica-card">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-20">
          <div className="max-w-xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-botanica-green font-semibold mb-3">Why Your Herbal Companion</p>
            <h2 className="font-serif text-3xl md:text-4xl text-botanica-ink leading-tight">
              A gentle, trustworthy way to explore natural remedies.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {highlights.map((h) => (
              <div key={h.title} className="bg-botanica-bg rounded-3xl p-7 border border-botanica-card hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(60,60,40,0.4)] transition-all duration-300">
                <span className="w-12 h-12 rounded-2xl bg-botanica-greenLight flex items-center justify-center mb-5">
                  <h.icon className="w-6 h-6 text-botanica-green" />
                </span>
                <h3 className="font-serif text-xl text-botanica-ink mb-2">{h.title}</h3>
                <p className="text-sm text-botanica-muted leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-botanica-green px-8 py-14 md:px-16 md:py-20 text-center">
          <Leaf className="absolute -top-6 -left-6 w-40 h-40 text-white/10 rotate-12" />
          <Leaf className="absolute -bottom-10 -right-4 w-48 h-48 text-white/10 -rotate-12" />
          <div className="relative">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Start your herbal journey today.</h2>
            <p className="text-white/80 max-w-lg mx-auto mb-8">
              Join a growing community of curious, careful herbalists. Free to start — upgrade anytime for unlimited Sage.
            </p>
            <button
              onClick={() => navigate('/membership')}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-botanica-greenDark font-semibold hover:bg-botanica-greenLight transition-colors"
            >
              Get started free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
